import { Check } from 'lucide-react';
import type { ThemeMeta } from '../lib/themes';
import { cn } from '../lib/cn';

type Props = {
  theme: ThemeMeta;
  active: boolean;
  onSelect: () => void;
  disabled?: boolean;
};

export const ThemeSwatch = ({ theme, active, onSelect, disabled }: Props) => {
  const { preview } = theme;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={active}
      aria-label={`Use ${theme.label} theme`}
      className={cn(
        'group flex flex-col gap-2 rounded-lg border p-3 text-left transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        active
          ? 'border-accent ring-1 ring-accent shadow-sm'
          : 'border-line hover:border-line-strong',
        disabled && 'cursor-wait opacity-60',
      )}
    >
      <div
        className="relative h-20 w-full overflow-hidden rounded-md border border-line"
        style={{ background: preview.bg }}
      >
        {/* Surface card */}
        <div
          className="absolute left-2 right-2 top-2 h-4 rounded"
          style={{ background: preview.surface }}
        />
        {/* Text rows */}
        <div
          className="absolute left-3 right-6 top-9 h-1.5 rounded"
          style={{ background: preview.text, opacity: 0.85 }}
        />
        <div
          className="absolute left-3 right-12 top-12 h-1 rounded"
          style={{ background: preview.text, opacity: 0.4 }}
        />
        {/* Accent pill */}
        <div
          className="absolute bottom-2 right-2 h-4 w-8 rounded-full"
          style={{ background: preview.accent }}
        />
        {active && (
          <div
            className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full text-white"
            style={{ background: preview.accent }}
          >
            <Check className="h-3 w-3" strokeWidth={3} />
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-primary">{theme.label}</span>
          {active && (
            <span className="rounded bg-accent-pale px-1.5 py-0.5 text-[10px] font-medium text-accent">
              Active
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-text-tertiary">{theme.description}</p>
      </div>
    </button>
  );
};
